import { ChevronDown, ChevronRight, GripVertical, Trash2 } from 'lucide-react'
import { useEffect, useRef } from 'react'
import type { ForceExpandState } from '@/hooks/use-force-expand-state'
import { useForceExpandState } from '@/hooks/use-force-expand-state'

interface CollapsibleMarkdownEditorProps {
  /** Current markdown content */
  content: string
  /** Callback when content changes */
  onChange: (content: string) => void
  /** Callback when delete is clicked */
  onDelete?: () => void
  /** Drag handle props from dnd-kit (optional) */
  dragHandleProps?: Record<string, unknown>
  /** Initial expanded state */
  defaultExpanded?: boolean
  /** Parent-controlled expand/collapse state */
  forceExpandState?: ForceExpandState | null
  /** Placeholder text for the textarea */
  placeholder?: string
  /** Label shown in the header */
  label?: string
}

/**
 * Collapsible markdown editor with drag handle and auto-resizing textarea.
 *
 * Features:
 * - Collapsible header with preview text when collapsed
 * - Auto-resizing textarea
 * - Optional drag handle for reordering
 * - Optional delete button
 * - Synchronizes with parent forceExpandState
 */
export function CollapsibleMarkdownEditor({
  content,
  onChange,
  onDelete,
  dragHandleProps,
  defaultExpanded = true,
  forceExpandState,
  placeholder = 'Write markdown content here...',
  label = 'Markdown',
}: CollapsibleMarkdownEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [isExpanded, setIsExpanded] = useForceExpandState(
    forceExpandState,
    defaultExpanded,
  )

  // Auto-resize textarea to fit content
  // biome-ignore lint/correctness/useExhaustiveDependencies: content triggers resize when changed
  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea && isExpanded) {
      textarea.style.height = 'auto'
      textarea.style.height = `${Math.max(textarea.scrollHeight, 100)}px`
    }
  }, [content, isExpanded])

  // Generate preview text (first ~80 chars, truncated at word boundary)
  const getPreview = () => {
    if (!content) return 'Empty markdown block'
    const trimmed = content.trim()
    if (trimmed.length <= 80) return trimmed
    const truncated = trimmed.slice(0, 80)
    const lastSpace = truncated.lastIndexOf(' ')
    return `${lastSpace > 40 ? truncated.slice(0, lastSpace) : truncated}...`
  }

  return (
    <div className="border-border rounded-lg border">
      <div
        className="border-border/50 flex cursor-pointer items-center gap-2 border-b px-3 py-2"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {dragHandleProps && (
          <button
            type="button"
            className="cursor-grab touch-none active:cursor-grabbing"
            {...dragHandleProps}
            aria-label="Drag to reorder"
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical className="text-muted-foreground h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground p-0.5 transition-colors"
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
        >
          {isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>
        <span className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
          {label}
        </span>
        {!isExpanded && (
          <span className="text-muted-foreground/70 ml-2 truncate text-xs italic">
            {getPreview()}
          </span>
        )}
        <div className="flex-1" />
        {onDelete && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onDelete()
            }}
            className="text-muted-foreground hover:text-destructive p-1 transition-colors"
            aria-label="Delete block"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {isExpanded && (
        <textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="bg-background min-h-[100px] w-full resize-y rounded-b-lg px-3 py-2 font-mono text-sm focus:outline-none"
        />
      )}
    </div>
  )
}
